import * as Primitive from '@radix-ui/react-dialog'
import { XIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { cn } from '../lib/cn'
import { dialogPanelOverlayClassName, dialogSurfaceStyles } from '../lib/dialog-panel-styles'
import { Button } from './button'
export function Dialog(props: ComponentProps<typeof Primitive.Root>) {
  return <Primitive.Root {...props} />
}
export function DialogTrigger(props: ComponentProps<typeof Primitive.Trigger>) {
  return <Primitive.Trigger {...props} />
}
export function DialogPortal(props: ComponentProps<typeof Primitive.Portal>) {
  return <Primitive.Portal {...props} />
}
export function DialogClose(props: ComponentProps<typeof Primitive.Close>) {
  return <Primitive.Close {...props} />
}
export function DialogOverlay({ className, ...props }: ComponentProps<typeof Primitive.Overlay>) {
  return (
    <Primitive.Overlay
      {...props}
      data-slot="dialog-overlay"
      className={cn(
        dialogPanelOverlayClassName,
        'motion-safe:data-[state=open]:animate-in motion-safe:data-[state=open]:fade-in-0',
        className,
      )}
    />
  )
}
type DialogContentProps = ComponentProps<typeof Primitive.Content> & {
  width?: 'compact' | 'regular' | 'wide'
  placement?: 'center' | 'start'
  overlayClassName?: string
} & (
    | { showCloseButton: false; closeLabel?: never }
    | { showCloseButton?: true; closeLabel: string }
  )
export function DialogContent({
  className,
  children,
  closeLabel,
  onFocusCapture,
  overlayClassName,
  showCloseButton = true,
  width = 'regular',
  placement = 'center',
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <Primitive.Content
        {...props}
        data-slot="dialog-content"
        className={cn(
          dialogSurfaceStyles(width, placement),
          '[&>[data-slot=form-layout]]:flex [&>[data-slot=form-layout]]:min-h-0 [&>[data-slot=form-layout]]:flex-1 [&>[data-slot=form-layout]]:flex-col [&>[data-slot=form-layout]]:overflow-hidden [&>[data-slot=form-layout]]:space-y-0',
          className,
        )}
        onFocusCapture={(event) => {
          onFocusCapture?.(event)
          if (!event.defaultPrevented)
            event.target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
        }}
      >
        {children}
        {showCloseButton ? (
          <Primitive.Close asChild>
            <Button
              appearance="ghost"
              tone="neutral"
              size="icon"
              aria-label={closeLabel}
              className="absolute top-3 right-3 z-20"
            >
              <XIcon className="size-4" aria-hidden="true" />
            </Button>
          </Primitive.Close>
        ) : null}
      </Primitive.Content>
    </DialogPortal>
  )
}
export function DialogHeader({
  className,
  appearance = 'plain',
  ...props
}: ComponentProps<'div'> & { appearance?: 'plain' | 'accent' }) {
  return (
    <div
      {...props}
      data-slot="dialog-header"
      className={cn(
        'sticky top-0 z-10 shrink-0 space-y-2 border-b border-border px-6 pt-6 pr-16 pb-5',
        appearance === 'accent' ? 'bg-panel-accent' : 'bg-card',
        className,
      )}
    />
  )
}
export function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="dialog-body"
      className={cn(
        'min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-6 py-5',
        className,
      )}
    />
  )
}
export function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      {...props}
      data-slot="dialog-footer"
      className={cn(
        'sticky bottom-0 z-10 shrink-0 border-t border-border bg-control px-6 py-4',
        className,
      )}
    />
  )
}
export function DialogTitle({ className, ...props }: ComponentProps<typeof Primitive.Title>) {
  return (
    <Primitive.Title
      {...props}
      className={cn('font-heading text-2xl font-normal leading-tight tracking-tight', className)}
    />
  )
}
export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof Primitive.Description>) {
  return (
    <Primitive.Description
      {...props}
      className={cn('text-sm leading-6 text-muted-foreground', className)}
    />
  )
}
